import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { CSSProperties } from 'react';
import { listMyRooms } from '../../api/chat';
import type { ChatRoomDto } from '../../types/chat';
import { useChatStore } from '../../store/chatStore';
import { useAuthStore } from '../../store/authStore';
import { useModalLeave } from '../../hooks/useModalLeave';
import { Avatar } from '../shared/Avatar';
import { Bot, ArrowRight } from '../../icons';
import { formatNotifTime } from '../../lib/notification-i18n';
import { tLayout, getLayoutLang, subscribeLayoutLang } from '../../lib/layout-i18n';
import './ChatDropdown.css';

/**
 * ⑥ 상태바 채팅 드롭다운 — 2026-08-11 사용자 요구.
 *
 *   "새로운 메시지·대화 내역이 있으면 상태바의 채팅에 숫자가 표기되고, 채팅을 누르면
 *    알림 내역과 같이 내역을 볼 수 있고, 해당 내역을 클릭하면 그 채팅으로 이동한다."
 *
 * 그래서 구조를 `NotificationDropdown` 과 의도적으로 **같게** 맞췄다(위로 열림·헤더+목록+
 * 전체보기 푸터·바깥 클릭 닫기·useModalLeave 언마운트 지연). 사용자가 두 버튼을 같은 것으로
 * 인지하기 때문에 상호작용이 달라지면 안 된다.
 *
 * 데이터: `listMyRooms` 1회 조회(열 때). 미읽음 수는 응답값이 아니라 **chatStore 를 우선**
 * 사용한다 — SSE(`onChatUnread`)가 실시간으로 올린 값이 서버 재조회보다 앞서 있을 수 있고,
 * 보고 있는 방은 ChatPage 가 이미 0 으로 지웠기 때문이다([[project-chat-read-receipts]]).
 *
 * 정렬: 미읽음 있는 방 우선 → 그 안에서 최근 활동순. 목록 자체가 "새 메시지 왔는지" 를
 * 알리는 장치라 안읽음이 아래로 밀리면 목적을 잃는다.
 *
 * ⚠ 미읽음 배지의 SSOT 는 chatStore 이고 그 동기화 소유자는 `LayoutNotificationHost` 하나다.
 *   여기서 SSE 를 새로 구독하지 않는다(이중 구독 = 미읽음 유실, design-20260717 R1).
 */

interface Props {
  open: boolean;
  onClose: () => void;
  /** 방 선택 — 호스트가 setActiveChat + navigate('/chat') 주입. */
  onOpenRoom: (roomId: string) => void;
  /** '전체 보기' → 채팅 화면으로. */
  onViewAll?: () => void;
  /** 여는 방향 — 'up' 은 하단 상태바 위로(기본은 알림 드롭다운과 같은 'down'). */
  placement?: 'down' | 'up';
}

const PREVIEW_LIMIT = 8;
/** 목록 조회 상한 — 미읽음 우선 정렬 뒤 PREVIEW_LIMIT 만 보여준다. */
const FETCH_LIMIT = 30;

export function ChatDropdown({ open, onClose, onOpenRoom, onViewAll, placement = 'down' }: Props): JSX.Element | null {
  useSyncExternalStore(subscribeLayoutLang, getLayoutLang, getLayoutLang);
  const [rooms, setRooms] = useState<ChatRoomDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const myId = useAuthStore((s) => s.user?.id?.toString() ?? '');
  const roomUnread = useChatStore((s) => s.roomUnread);
  // 열려 있는 동안 새 메시지가 오면 목록도 따라 갱신 — 합계가 바뀐 순간을 트리거로 쓴다.
  const unreadTotal = useChatStore((s) => s.chatUnreadTotal);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { shouldRender, isLeaving } = useModalLeave(open, 160);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    listMyRooms({ limit: FETCH_LIMIT })
      .then((res) => {
        if (!cancelled) setRooms(res.rooms ?? []);
      })
      .catch(() => {
        if (!cancelled) setError(tLayout('layout.chatdrop.error'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, unreadTotal]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) onClose();
    };
    const t = setTimeout(() => document.addEventListener('mousedown', onPointer), 0);
    return () => { clearTimeout(t); document.removeEventListener('mousedown', onPointer); };
  }, [open, onClose]);

  /**
   * 내역 선택 — 2026-08-12 사용자 요구: "해당 내역을 누르면 숫자가 사라져야 한다."
   *
   * 배지를 **누른 그 자리에서** 지운다. 종전엔 방 이동 후 ChatPage 가 읽음 처리할 때까지
   * 숫자가 남아, 목록이 닫히는 순간까지 안 읽은 것처럼 보였다.
   *
   * 두 군데를 함께 지운다:
   *   - `clearRoomUnread` : 전역 SSOT(상태바 합계 배지·방 목록 배지).
   *   - 로컬 `rooms`      : 이 목록이 서버 응답의 `unreadCount` 로 폴백하는 경로 차단.
   *     store 에 그 방 항목이 아직 없으면 clearRoomUnread 가 no-op 이라 폴백 값이 되살아난다.
   *
   * ⚠ 여기서 서버 읽음 처리(`markRoomRead`)는 **하지 않는다.** ChatPage 가 read-states 조회 →
   *   읽음 처리 순서를 명시적으로 직렬화해 "여기부터 안 읽었습니다" 구분선을 지키는데
   *   ([[project-chat-read-receipts]]), 여기서 먼저 쏘면 그 앵커가 통째로 사라진다.
   *   서버 반영은 방을 열면서 ChatPage 가 제 순서대로 한다.
   */
  const pick = useCallback((roomId: string) => {
    useChatStore.getState().clearRoomUnread(roomId);
    setRooms((prev) => prev.map((r) => (r.id === roomId ? { ...r, unreadCount: 0 } : r)));
    onClose();
    onOpenRoom(roomId);
  }, [onClose, onOpenRoom]);

  if (!shouldRender) return null;

  const unreadOf = (r: ChatRoomDto): number => roomUnread[r.id] ?? r.unreadCount ?? 0;
  const items = [...rooms]
    .sort((a, b) => {
      const ua = unreadOf(a) > 0 ? 1 : 0;
      const ub = unreadOf(b) > 0 ? 1 : 0;
      if (ua !== ub) return ub - ua; // 미읽음 방 우선
      return timeOf(b) - timeOf(a); // 그 안에서 최근 활동순
    })
    .slice(0, PREVIEW_LIMIT);
  const unreadRooms = items.filter((r) => unreadOf(r) > 0).length;

  return (
    <div
      className={`chat-dropdown chat-dropdown--${placement}${isLeaving ? ' chat-dropdown--leaving' : ''}`}
      ref={containerRef}
      role="dialog"
      aria-label={tLayout('layout.chatdrop.aria_list')}
      aria-hidden={isLeaving || undefined}
      // 상태바 버튼의 형제로 렌더되므로 내부 클릭이 버튼 토글로 버블링되지 않게 차단.
      onClick={(e) => e.stopPropagation()}
    >
      <div className="chat-dropdown-header">
        <h3 className="chat-dropdown-title">{tLayout('layout.chatdrop.title')}</h3>
        {unreadRooms > 0 && (
          <span className="chat-dropdown-count">{tLayout('layout.chatdrop.unread_badge', { n: unreadRooms })}</span>
        )}
      </div>

      <div className="chat-dropdown-list scrollbar-thin">
        {loading && <div className="chat-dropdown-status">{tLayout('layout.chatdrop.loading')}</div>}
        {error && <div className="chat-dropdown-status chat-dropdown-error">{error}</div>}
        {!loading && !error && items.length === 0 && (
          <div className="chat-dropdown-empty">
            <span className="chat-dropdown-empty-ico" aria-hidden>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>
            </span>
            {tLayout('layout.chatdrop.empty')}
          </div>
        )}
        {!loading && !error && items.map((room, i) => {
          const unread = unreadOf(room);
          const isAi = room.memberCount === 1;
          const others = (room.memberPreview ?? []).filter((m) => m.userId !== myId);
          const lead = others[0] ?? room.memberPreview?.[0] ?? null;
          const name = room.name ?? lead?.name ?? tLayout('layout.chatdrop.no_name');
          return (
            <button
              key={room.id}
              type="button"
              className={`chat-dropdown-item${unread > 0 ? ' chat-dropdown-item-unread' : ''}`}
              style={{ animationDelay: `${Math.min(i, 8) * 28}ms` } as CSSProperties}
              onClick={() => pick(room.id)}
              aria-label={unread > 0 ? `${name} — ${tLayout('layout.chatdrop.unread_badge', { n: unread })}` : name}
            >
              {isAi ? (
                <span className="chat-dropdown-ai" aria-label={tLayout('layout.chatdrop.ai_room')}><Bot size={15} /></span>
              ) : (
                <Avatar
                  userId={Number(lead?.userId ?? room.id)}
                  name={lead?.name ?? name}
                  src={lead?.avatarUrl ?? null}
                  size="sm"
                />
              )}
              <span className="chat-dropdown-item-body">
                <span className="chat-dropdown-item-title">{name}</span>
                <span className="chat-dropdown-item-meta">
                  {room.lastMessageAt ? formatNotifTime(room.lastMessageAt) : ''}
                </span>
              </span>
              {unread > 0 && (
                <span className="chat-dropdown-badge">{unread > 99 ? '99+' : unread}</span>
              )}
            </button>
          );
        })}
      </div>

      <button
        type="button"
        className="chat-dropdown-viewall"
        onClick={() => { onClose(); onViewAll?.(); }}
      >
        {tLayout('layout.chatdrop.view_all')} <ArrowRight size={13} aria-hidden />
      </button>
    </div>
  );
}

/** 정렬 키 — 마지막 메시지 시각(없으면 0). NaN 방어 포함. */
function timeOf(r: ChatRoomDto): number {
  if (!r.lastMessageAt) return 0;
  const t = new Date(r.lastMessageAt).getTime();
  return Number.isNaN(t) ? 0 : t;
}
